import { useState } from 'react';
import { App as AntdApp, Button, Input, Modal, Space, Tag, Tooltip, Typography } from 'antd';
import { CloseOutlined, PlusOutlined, StarFilled } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { SavedView } from '@/hooks/useSavedViews';

const { Text } = Typography;

export interface SavedViewsBarProps<T> {
  views: SavedView<T>[];
  activeId: string | null;
  onSave: (name: string) => void;
  onApply: (id: string) => void;
  onDelete: (id: string) => void;
  isPreset: (id: string) => boolean;
  /** Translation namespace for preset name keys (default: 'common'). */
  presetNs?: string;
}

/**
 * S4-T04 — Saved table views chip bar.
 *
 * Renders all available views (system presets + user-saved) as toggleable
 * chips. Active view is highlighted; non-preset chips show a delete affordance
 * on hover. "Save current view…" opens a name prompt modal.
 *
 * Place inside the page's filter `<Card>`, after the filter inputs.
 */
function SavedViewsBar<T>({
  views,
  activeId,
  onSave,
  onApply,
  onDelete,
  isPreset,
  presetNs = 'common',
}: SavedViewsBarProps<T>) {
  const { t } = useTranslation([presetNs, 'common']);
  const { message } = AntdApp.useApp();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onSave(trimmed);
    setName('');
    setOpen(false);
    void message.success(t('common:savedViews.toast.saved', { name: trimmed }));
  };

  const handleDelete = (id: string, displayName: string) => {
    onDelete(id);
    void message.success(t('common:savedViews.toast.deleted', { name: displayName }));
  };

  const renderName = (view: SavedView<T>): string => {
    if (view.source === 'preset') {
      // Presets carry a translation key; fall back to the key if missing.
      const translated = t(view.name, { ns: presetNs, defaultValue: view.name });
      return translated;
    }
    return view.name;
  };

  return (
    <>
      <Space wrap size={4} align="center">
        <Text type="secondary" style={{ fontSize: 12, marginInlineEnd: 4 }}>
          {t('common:savedViews.label')}
        </Text>
        {views.map((v) => {
          const active = v.id === activeId;
          const preset = isPreset(v.id);
          return (
            <Tag.CheckableTag
              key={v.id}
              checked={active}
              onChange={() => onApply(v.id)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                paddingInline: 8,
                paddingBlock: 2,
                borderRadius: 12,
                cursor: 'pointer',
              }}
            >
              {preset && <StarFilled style={{ fontSize: 9, opacity: 0.65 }} />}
              <span>{renderName(v)}</span>
              {!preset && (
                <Tooltip title={t('common:savedViews.delete')}>
                  <CloseOutlined
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(v.id, renderName(v));
                    }}
                    style={{
                      fontSize: 9,
                      marginInlineStart: 4,
                      opacity: 0.55,
                      cursor: 'pointer',
                    }}
                  />
                </Tooltip>
              )}
            </Tag.CheckableTag>
          );
        })}
        <Button
          size="small"
          type="dashed"
          icon={<PlusOutlined />}
          onClick={() => setOpen(true)}
        >
          {t('common:savedViews.saveCurrent')}
        </Button>
      </Space>

      <Modal
        title={t('common:savedViews.saveTitle')}
        open={open}
        onOk={handleSave}
        onCancel={() => {
          setOpen(false);
          setName('');
        }}
        okText={t('common:savedViews.save')}
        cancelText={t('common:savedViews.cancel')}
        okButtonProps={{ disabled: !name.trim() }}
        destroyOnHidden
      >
        <Input
          placeholder={t('common:savedViews.namePlaceholder')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onPressEnter={handleSave}
          maxLength={40}
          autoFocus
        />
      </Modal>
    </>
  );
}

export default SavedViewsBar;
